import { useState } from "react";
import { cx } from "@/utils/cx";

type OrganizationAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface OrganizationAvatarProps {
  name: string;
  logoUrl?: string | null;
  size?: OrganizationAvatarSize;
  className?: string;
}

const styles = {
  xs: {
    root: "size-6 rounded-md",
    initials: "text-xs font-semibold",
  },
  sm: {
    root: "size-8 rounded-lg",
    initials: "text-sm font-semibold",
  },
  md: {
    root: "size-10 rounded-lg",
    initials: "text-md font-semibold",
  },
  lg: {
    root: "size-12 rounded-xl",
    initials: "text-lg font-semibold",
  },
  xl: {
    root: "size-16 rounded-xl",
    initials: "text-xl font-semibold",
  },
};

const backgroundColors = [
  "bg-blue-600",
  "bg-green-600",
  "bg-purple-600",
  "bg-orange-600",
  "bg-pink-600",
  "bg-indigo-600",
  "bg-teal-600",
  "bg-cyan-600",
  "bg-red-600",
  "bg-yellow-600",
];

function getBackgroundColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % backgroundColors.length;
  return backgroundColors[index];
}

function getInitial(name: string): string {
  return name.charAt(0).toUpperCase();
}

export const OrganizationAvatar = ({
  name,
  logoUrl,
  size = "md",
  className,
}: OrganizationAvatarProps) => {
  const [isFailed, setIsFailed] = useState(false);

  const renderContent = () => {
    if (logoUrl && !isFailed) {
      return (
        <img
          className="size-full object-cover"
          style={{ borderRadius: "inherit" }}
          src={logoUrl}
          alt={name}
          onError={() => setIsFailed(true)}
        />
      );
    }

    return (
      <span className={cx("text-white", styles[size].initials)}>
        {getInitial(name)}
      </span>
    );
  };

  const showFallback = !logoUrl || isFailed;

  return (
    <div
      data-organization-avatar
      className={cx(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden",
        showFallback && getBackgroundColor(name),
        styles[size].root,
        className,
      )}
    >
      {renderContent()}
    </div>
  );
};
